import "./SparkChart.scss";
import { Brand0, Brand30, Brand60 } from "../../utils/colors";
import { downloadPNG, exportMultiSheetToExcel, Sheet } from "../../utils/download-utils";
import { getMetricFormatter } from "../../utils/metric-format-utils";
import { LINE_STROKE_WIDTH } from "../../TVADCrossChannel/homePageConstants";
import { makePrettyDateRange } from "../../CrossChannel/crossChannelUtils";
import { MdCallMade } from "react-icons/md";
import { METRIC_OPTIONS, METRIC_TO_PRETTY_NAME } from "../../CrossChannel/crossChannelConstants";
import { Metric, Metrics, MetricTotalsByDate } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import { XAxis, YAxis, Tooltip, Line, Area, ComposedChart } from "recharts";
import * as R from "ramda";
import AutoSizer from "react-virtualized-auto-sizer";
import ChartTooltipV2 from "../Charts/ChartTooltip/ChartTooltipV2";
import React, { useMemo, useState, useCallback } from "react";
import useLocation from "../../utils/hooks/useLocation";
import {
  DownloadDropdown,
  BPMTable,
  Dropdown,
  DropdownToggleType,
  GraphTableToggleButton,
} from "..";
import { formatDateLabel } from "../../utils/date-utils";
import { sharedColumnMetaDataMap } from "../../SingleChannel/Channels/sharedPerformanceUtils";

enum MetricTotalType {
  TOTAL = "total",
  AVERAGE = "average",
  OVERALL = "overall",
}

interface JoinedData {
  date: string;
  date_other: string;
  metrics: Metrics;
  metrics_other: Metrics;
}

interface SparkChartProps {
  data: MetricTotalsByDate[]; // Primary date range
  otherData: MetricTotalsByDate[]; // Comparison date range
  defaultMetric?: Metric;
  metricOptions?: { label: string; value: Metric }[];
  pngFileIdentifiers?: string[];
  pngLabel?: string;
  dataInfo?: Record<string, string>[];
}

const isInverted = (metric: string) => ["cpx", "cpc", "cpm"].includes(metric);

/**
 * Combine data and otherData into one array, even though they can be of differing lengths/date ranges.
 * Add "_other" suffix to the keys of the comparison data to avoid conflicts.
 */
const getJoinedData = (
  data: MetricTotalsByDate[],
  otherData: MetricTotalsByDate[]
): JoinedData[] => {
  const maxLength = Math.max(data.length, otherData.length);

  let joined: JoinedData[] = [];
  for (let i = 0; i < maxLength; i++) {
    const item = data[i] || {};
    // Rename keys of comparison data
    const otherItem = R.keys(otherData[i] || {}).reduce((acc, key) => {
      acc[`${key}_other`] = (otherData[i] || {})[key];
      return acc;
    }, {});

    joined.push({
      ...item,
      ...otherItem,
    } as JoinedData);
  }

  return joined;
};

const SparkChart: React.FC<SparkChartProps> = React.memo(
  ({
    data,
    otherData,
    defaultMetric,
    metricOptions = METRIC_OPTIONS,
    pngFileIdentifiers = [],
    pngLabel,
    dataInfo,
  }) => {
    const [selectedMetric, setSelectedMetric] = useState<Metric>(defaultMetric || Metric.SPEND);
    const [graphView, setGraphView] = useState(true);
    const [hover, setHover] = useState<boolean>(false);
    const { company } = useLocation();
    const metricFormatter = useMemo(() => getMetricFormatter(company), [company]);

    const isOverallMetric = useMemo(
      () => (sharedColumnMetaDataMap[selectedMetric] || {}).aggregator !== undefined,
      [selectedMetric]
    );

    const [valueToggle, setValueToggle] = useState<string>(
      isOverallMetric ? MetricTotalType.OVERALL : MetricTotalType.TOTAL
    );

    const { total, otherTotal, percentChange, average, otherAverage } = useMemo(() => {
      let total;
      let otherTotal;

      if (isOverallMetric) {
        total = calculateOverall(data, selectedMetric);
        otherTotal = calculateOverall(otherData, selectedMetric);
      } else {
        total = calculateTotal(data, selectedMetric);
        otherTotal = calculateTotal(otherData, selectedMetric);
      }

      const percentChange = Math.round(((total - otherTotal) / otherTotal) * 100);
      const average = total / data.length;
      const otherAverage = otherTotal / otherData.length;

      return {
        total,
        otherTotal,
        percentChange: isFinite(percentChange) ? percentChange : "--",
        average,
        otherAverage,
      };
    }, [data, otherData, selectedMetric, isOverallMetric]);

    const metricInverted = useMemo(() => isInverted(selectedMetric), [selectedMetric]);

    const metricsToPrettyNameMap = useMemo(() => {
      const prettyNameMap = {};
      for (let metric of metricOptions) {
        prettyNameMap[metric.value] = metric.label;
      }
      return prettyNameMap;
    }, [metricOptions]);

    const [headers, superHeaders] = useMemo(() => {
      let headers: any[] = [
        {
          label: "Date",
          name: "date",
          width: 60,
          nonInteractive: true,
          renderer: (item: JoinedData) => formatDateLabel(item.date, {}, "", true) || "-",
        },
        {
          label: metricsToPrettyNameMap[selectedMetric],
          name: selectedMetric,
          nonInteractive: true,
          flex: 1,
          renderer: (item: JoinedData) =>
            metricFormatter(selectedMetric)(R.pathOr(0, ["metrics", selectedMetric], item)),
        },
        {
          label: "% ∆",
          name: "percentChange",
          nonInteractive: true,
          width: 35,
          renderer: (item: JoinedData) => {
            const metric = R.pathOr(0, ["metrics", selectedMetric], item);
            const otherMetric = R.pathOr(0, ["metrics_other", selectedMetric], item);
            const percentChange = Math.round(((metric - otherMetric) / otherMetric) * 100);
            // Invert the color for cpx, cpc, and cpm
            // because a decrease in these metrics is good
            const color =
              (percentChange < 0 && !metricInverted) || (percentChange > 0 && metricInverted)
                ? "red"
                : "green";
            return percentChange ? (
              <div style={{ color }}>{isFinite(percentChange) ? `${percentChange}%` : "--"}</div>
            ) : (
              "--"
            );
          },
        },
        {
          label: metricsToPrettyNameMap[selectedMetric],
          name: `${selectedMetric}_other`,
          nonInteractive: true,
          flex: 1,
          renderer: (item: JoinedData) =>
            metricFormatter(selectedMetric)(R.pathOr(0, ["metrics_other", selectedMetric], item)),
        },
        {
          label: "Date",
          name: "date_other",
          width: 60,
          nonInteractive: true,
          renderer: (item: JoinedData) => formatDateLabel(item.date_other, {}, "", true) || "-",
        },
      ];
      const superHeaders: any[] = [
        { span: 2, data: "Primary Period" },
        { span: 1 },
        { span: 2, data: "Comparison Period" },
      ];

      return [headers, superHeaders];
    }, [metricInverted, metricsToPrettyNameMap, selectedMetric, metricFormatter]);

    const joinedData = useMemo(() => getJoinedData(data, otherData), [data, otherData]);

    const totals = useMemo(() => {
      return {
        date: "",
        date_other: "",
        percentChange: "",
        [selectedMetric]:
          valueToggle === MetricTotalType.TOTAL || valueToggle === MetricTotalType.OVERALL
            ? metricFormatter(selectedMetric)(total)
            : metricFormatter(selectedMetric)(average),
        [`${selectedMetric}_other`]:
          valueToggle === MetricTotalType.TOTAL || valueToggle === MetricTotalType.OVERALL
            ? metricFormatter(selectedMetric)(otherTotal)
            : metricFormatter(selectedMetric)(otherAverage),
      };
    }, [average, otherAverage, otherTotal, selectedMetric, total, valueToggle, metricFormatter]);

    const totalsRenderer = ({ data, style = {}, classes = [] as string[] }) => {
      return (
        <div style={style} className={[...classes, "grandTotalCell"].join(" ")}>
          {data}
        </div>
      );
    };

    const fileNameIdentifiers = useMemo(() => {
      const prettySelectedMetric =
        METRIC_OPTIONS.find(item => item.value === selectedMetric)?.label || selectedMetric;
      const subHeaderValue =
        !isOverallMetric && valueToggle === MetricTotalType.TOTAL ? "Total" : "Overall";
      const primaryDateRange = data.length ? `${data[0].date}–${data[data.length - 1].date}` : "";
      const comparisonDateRange = otherData.length
        ? `vs_${otherData[0].date}–${otherData[otherData.length - 1].date}`
        : "";
      return [
        ...pngFileIdentifiers,
        prettySelectedMetric,
        subHeaderValue,
        primaryDateRange,
        comparisonDateRange,
      ];
    }, [data, otherData, pngFileIdentifiers, isOverallMetric, valueToggle, selectedMetric]);

    const excelDownload = useCallback(() => {
      const resolvedFileName = fileNameIdentifiers.join("_");
      const exportData = processExport([...data, ...otherData]);

      let sheets: Sheet[] = [{ name: "Spark Chart", data: exportData }];
      if (dataInfo) {
        sheets.push({ name: "Data Info", data: dataInfo });
      }

      exportMultiSheetToExcel(sheets, resolvedFileName);
    }, [data, otherData, fileNameIdentifiers, dataInfo]);

    const pngDownload = useCallback(async () => {
      await downloadPNG(
        `.sparkChartContainer${defaultMetric ? `.${defaultMetric}` : ""}`,
        fileNameIdentifiers,
        pngLabel,
        [".sparkChartActions"]
      );
    }, [pngLabel, defaultMetric, fileNameIdentifiers]);

    return (
      <div
        className={`sparkChartContainer ${hover ? "hover" : ""} ${defaultMetric}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="sparkChartHeader">
          <div className="sparkChartTitle">
            <Dropdown
              type={DropdownToggleType.WIDGET_TITLE}
              value={selectedMetric}
              options={R.sort((a, b) => a.label.localeCompare(b.label), metricOptions)}
              onChange={option => {
                setSelectedMetric(option);
                setValueToggle(
                  (sharedColumnMetaDataMap[option] || {}).aggregator
                    ? MetricTotalType.OVERALL
                    : MetricTotalType.TOTAL
                );
              }}
            />
          </div>
          <div className="sparkChartActions">
            {hover && (
              <>
                <GraphTableToggleButton
                  graphView={graphView}
                  setGraphView={setGraphView}
                  widgetName={`spark_chart_${selectedMetric}`}
                />
                {data && (
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownload, pngDownload]}
                    disabledMenuOptions={
                      graphView ? { XLSX: false, PNG: false } : { XLSX: false, PNG: true }
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div className="sparkChartSubHeader">
          {graphView && (
            <div className="value">
              {metricFormatter(selectedMetric)(
                valueToggle === MetricTotalType.AVERAGE ? average : total
              )}
            </div>
          )}
          <div className="sparkChartToggleSelector">
            {isOverallMetric && (valueToggle === MetricTotalType.OVERALL || hover) && (
              <div
                className={`sparkChartToggleItem ${
                  valueToggle === MetricTotalType.OVERALL ? "selected" : "deselected"
                }`}
                onClick={() => setValueToggle(MetricTotalType.OVERALL)}
              >
                OVERALL
              </div>
            )}
            {!isOverallMetric && (valueToggle === MetricTotalType.TOTAL || hover) && (
              <div
                className={`sparkChartToggleItem ${
                  valueToggle === MetricTotalType.TOTAL ? "selected" : "deselected"
                }`}
                onClick={() => setValueToggle(MetricTotalType.TOTAL)}
              >
                TOTAL
              </div>
            )}
            {!isOverallMetric && (valueToggle === MetricTotalType.AVERAGE || hover) && (
              <div
                className={`sparkChartToggleItem ${
                  valueToggle === MetricTotalType.AVERAGE ? "selected" : "deselected"
                }`}
                onClick={() => setValueToggle(MetricTotalType.AVERAGE)}
              >
                DAILY AVG
              </div>
            )}
          </div>
        </div>
        <div className={`sparkChartBody ${graphView ? "graphView" : "tableView"}`}>
          {graphView ? (
            <AutoSizer>
              {({ width, height }) => (
                <ComposedChart
                  width={width}
                  height={height}
                  data={joinedData}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                >
                  <Tooltip
                    content={({ payload }) => {
                      const data = payload?.[0]?.payload || {};

                      const primaryHeaderLabel = formatDateLabel(data?.date || "", {}, "Day", true);
                      const comparisonHeaderLabel = formatDateLabel(
                        data?.date_other || "",
                        {},
                        "Day",
                        true
                      );

                      const primaryMetricValue = R.pathOr(0, ["metrics", selectedMetric], data);
                      const comparisonMetricValue = R.pathOr(
                        0,
                        ["metrics_other", selectedMetric],
                        data
                      );

                      const items = [
                        {
                          label: metricsToPrettyNameMap[selectedMetric],
                          value: metricFormatter(selectedMetric)(primaryMetricValue),
                          color: Brand60,
                        },
                      ];

                      const comparisonItems = [
                        {
                          label: metricsToPrettyNameMap[selectedMetric],
                          value: metricFormatter(selectedMetric)(comparisonMetricValue),
                          color: "#000000",
                        },
                      ];

                      return (
                        <ChartTooltipV2
                          headers={[primaryHeaderLabel, comparisonHeaderLabel]}
                          items={items}
                          comparisonItems={comparisonItems}
                        />
                      );
                    }}
                    allowEscapeViewBox={{ x: true }}
                  />
                  <XAxis
                    hide={true}
                    dataKey={R.length(data) > R.length(otherData) ? "date" : "date_other"}
                  />
                  <YAxis hide={true} />
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="1%" stopColor={Brand30} />
                      <stop offset="95%" stopColor={Brand0} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="linear"
                    name={selectedMetric}
                    dataKey={`metrics.${selectedMetric}`}
                    stackId="1"
                    activeDot={false}
                    fillOpacity={1}
                    strokeWidth={3}
                    stroke={"#8254FF"}
                    isAnimationActive={false}
                    fill="url(#gradient)"
                  />
                  {hover && (
                    <Line
                      name={`${selectedMetric}_other`}
                      dataKey={`metrics_other.${selectedMetric}`}
                      type="linear"
                      strokeWidth={LINE_STROKE_WIDTH}
                      activeDot={false}
                      dot={false}
                      isAnimationActive={false}
                      stroke="#1F003F"
                      strokeDasharray="4"
                    />
                  )}
                </ComposedChart>
              )}
            </AutoSizer>
          ) : (
            <BPMTable
              data={joinedData}
              headers={headers}
              superHeaders={superHeaders}
              filterBar={false}
              totals={totals}
              totalsRenderer={totalsRenderer}
              headerHeight={30}
              superHeaderHeight={30}
              bottomRowHeight={30}
            />
          )}
        </div>
        {graphView && (
          <div className="sparkChartFooter">
            {hover ? (
              <div className="comparison">
                <div className="range">
                  {`${makePrettyDateRange(
                    (otherData[0] || {}).date,
                    (otherData[otherData.length - 1] || {}).date
                  )} ${valueToggle === MetricTotalType.AVERAGE ? "Avg." : ""} ${
                    metricsToPrettyNameMap[selectedMetric]
                  }:`}
                </div>
                <div className="value">
                  {metricFormatter(selectedMetric)(
                    valueToggle === MetricTotalType.OVERALL || valueToggle === MetricTotalType.TOTAL
                      ? otherTotal
                      : otherAverage
                  )}
                </div>
              </div>
            ) : (
              <div></div>
            )}
            <div
              className={`percentChange ${
                ((percentChange as number) < 0 && !metricInverted) ||
                ((percentChange as number) > 0 && metricInverted)
                  ? "inverted"
                  : ""
              }`}
            >
              <MdCallMade
                className={`percentArrow ${(percentChange as number) < 0 ? "inverted" : ""}`}
              />{" "}
              {`${percentChange}%`}
            </div>
          </div>
        )}
      </div>
    );
  }
);

const processExport = (data: MetricTotalsByDate[]) => {
  const result = data.map(({ date, metrics }) => {
    const { impressions, spend, cpm, clicks } = metrics;

    const prettyNameMetrics = {};

    Object.entries(metrics).forEach(([key, value]) => {
      prettyNameMetrics[METRIC_TO_PRETTY_NAME[key]] = value;
    });

    return {
      date,
      [METRIC_TO_PRETTY_NAME[Metric.IMPRESSIONS]]: impressions,
      [METRIC_TO_PRETTY_NAME[Metric.SPEND]]: spend,
      [METRIC_TO_PRETTY_NAME[Metric.CPM]]: cpm,
      [METRIC_TO_PRETTY_NAME[Metric.CLICKS]]: clicks,
      ...prettyNameMetrics,
    };
  });

  return result;
};

const calculateTotal = (data: MetricTotalsByDate[], metric: Metric) => {
  const total = data.reduce((acc, curr) => acc + R.pathOr(0, ["metrics", metric], curr), 0);
  return total;
};

// For metrics that are a calculation of two other metrics (e.g., CPC, CPM, CPX, ROAS, etc.),
const calculateOverall = (data: MetricTotalsByDate[], metric: Metric) => {
  const totals = {};
  for (let item of data) {
    const { metrics } = item;
    for (let metric of Object.keys(metrics)) {
      totals[metric] = (totals[metric] || 0) + metrics[metric];
    }
  }

  const metricInfo = sharedColumnMetaDataMap[metric];
  const overall =
    metricInfo && typeof metricInfo.aggregator === "function" ? metricInfo.aggregator(totals) : 0;
  return overall;
};

export default SparkChart;
